import {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

/**
 * The review page's focus mode: every panel around the grid folds down to its
 * minimal form — the app sidebar to its icon rail, the actions, pricelist and
 * comments panels to their strips, the review's settings to a one-line
 * summary — so the grid gets the room, without leaving the page. Leaving focus
 * puts each panel back the way it was.
 */
export interface ReviewFocus {
  focused: boolean;
  setFocused: (focused: boolean) => void;
}

/** Provided by the review page; absent elsewhere (the catalog has no focus mode). */
export const ReviewFocusContext = createContext<ReviewFocus | null>(null);

export function useReviewFocus(): ReviewFocus | null {
  return useContext(ReviewFocusContext);
}

/**
 * A panel's open state that folds down while focus mode is on and comes back
 * as it was when focus mode ends. The panel stays the reader's to toggle in
 * between: toggles made in focus land on a separate state that starts folded
 * each time focus begins, so the state from before focus is never touched.
 */
export function usePanelOpenOutsideFocus(
  initialOpen: boolean,
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [open, setOpen] = useState(initialOpen);
  return useFocusFoldedOpen(open, setOpen);
}

/**
 * {@link usePanelOpenOutsideFocus} over an open state the caller holds — for a
 * panel that keeps it with other state (the comments panel saves it with its
 * filters). `open` is only ever the state outside focus.
 */
export function useFocusFoldedOpen(
  open: boolean,
  setOpen: Dispatch<SetStateAction<boolean>>,
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const focused = useReviewFocus()?.focused ?? false;
  const [openInFocus, setOpenInFocus] = useState(false);
  const [seenFocused, setSeenFocused] = useState(focused);
  if (focused !== seenFocused) {
    setSeenFocused(focused);
    if (focused) setOpenInFocus(false);
  }
  return focused ? [openInFocus, setOpenInFocus] : [open, setOpen];
}
